import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Box, Button, Divider, Paper, styled, useMediaQuery, useTheme} from '@mui/material';
import Avatar from '@mui/material/Avatar';
import {
    confirmProfile,
    resetUpdatingStatus,
    retrieveBasicInfo,
    retrieveContactRelations,
    retrieveCountries,
    retrieveGroupsForBasicDetails,
    retrieveProfile,
    updateBasicInfo,
    updateStudentByParent
} from '../../ProfilesSlice';
import {showSnackbar} from '../../../../../AppLayout/ApplicationSlice';
import {basicInfoFields} from '../../const/BasicInfoFields';
import ProfileStatus from '../../Components/ProfileStatus';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import {useParams} from 'react-router-dom';
import {useConfirm} from 'material-ui-confirm';
import {ROLES} from '../../../../../constants/roles';
import useUser from '../../../../../utility/hooks/useUser';
import BasicInfoFields from './BasicInfoFields';
import {formatDate} from '../../../../../utility/dateUtil';
import {STUDENT_PROFILE_CONFIRMATION_TYPES} from '../../const/StudentProfileConfirmationTypes';
import PropTypes from 'prop-types';

const StudentDataBox = styled(Box)({
    display: 'flex', minHeight: '300px', flexDirection: 'row', gap: 30, marginTop: 10, flexWrap: 'wrap'
});

const StudentMainInfo = styled(Paper)({
    width: '100%',
    minHeight: '120px',
    display: 'flex',
    justifyContent: 'space-around',
    marginTop: '10px',
    alignItems: 'center',
    padding: '10px',
    gap: 10,
});

const StudentMainFields = styled(Box)({
    display: 'flex',
    flexGrow: 1,
});

const EducationInfoSection = styled(Paper)({
    flex: 1, display: 'flex', flexDirection: 'column', paddingBottom: '10px'
});

const EducationInfoFields = styled(Box)({
    height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-around'
});

const HomeAddressContainer = styled(Paper)({
    flex: 1, display: 'flex', flexDirection: 'column', minWidth: '250px'
});

const AddressSectionFields = styled(Grid)({
    display: 'flex', flexDirection: 'column', justifyContent: 'space-around', gap: '10px'
});

const ActionButtonsBox = styled(Box)({
    display: 'flex', gap: '10px', justifyContent: 'end', paddingTop: '10px',
});

const ActionButton = styled(Button)({
    width: '100px'
});

const BasicInfoTab = ({readonly}) => {
    const {user} = useUser();
    const [isEditMode, setIsEditMode] = useState(false);
    const [profileData, setProfileData] = useState({});
    const {studentId} = useParams();
    const [errors, setErrors] = useState({});
    const {
        selectedProfile,
        updatingSuccessful,
        countries,
        houseGroups,
        yearGroups,
        formGroups,
        boarderGroups,
        currentSchool
    } = useSelector(state => state.profilesInfo);
    const dispatch = useDispatch();
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const confirm = useConfirm();

    useEffect(() => {
        dispatch(retrieveCountries());
        dispatch(retrieveBasicInfo({studentId}));
        dispatch(retrieveContactRelations());
        dispatch(retrieveProfile({studentId})).then((res) => {
            dispatch(retrieveGroupsForBasicDetails({isApplicant: res.payload?.isApplicant}));
        });
    }, []);

    useEffect(() => {
        if (selectedProfile.basicInfo) {
            setProfileData(selectedProfile.basicInfo);
        }
    }, [selectedProfile.basicInfo]);

    useEffect(() => {
        showNotification(updatingSuccessful);
        if (updatingSuccessful) {
            dispatch(resetUpdatingStatus());
            dispatch(retrieveBasicInfo({studentId: selectedProfile.id}));
            setIsEditMode(false);
        }

        return () => {
            dispatch(resetUpdatingStatus());
        };
    }, [updatingSuccessful]);

    useEffect(() => {
        if (!isEditMode) {
            setErrors({});
        }
    }, [isEditMode]);

    const buildSelectOptions = () => {
        return {
            intakeId: yearGroups.map(element => {
                return {value: element.id, title: element.name};
            }),
            yearOfEntryId: yearGroups.map(element => {
                return {value: element.id, title: element.name};
            }),
            formId: formGroups.map(element => {
                return {value: element.id, title: element.name};
            }),
            houseId: houseGroups.map(element => {
                return {value: element.id, title: element.name};
            }),
            boarderId: boarderGroups.map(element => {
                return {value: element.id, title: element.name};
            }),
        };
    };

    const buildFields = () => {
        return [selectedProfile.isApplicant
            ? {
                id: 'profile_intakeGroup',
                title: 'Intake Group',
                key: 'intakeId',
                type: 'select',
                isRequired: true
            }
            : {
                id: 'profile_yearGroup',
                title: 'Year Group',
                key: 'yearOfEntryId',
                type: 'select',
                isRequired: true
            },
            ...basicInfoFields.educationInfo];
    };

    const showNotification = success => success !== undefined && dispatch({
        true: () => showSnackbar({
            message: 'Basic student information updated successfully', severity: 'success'
        }),
        false: () => showSnackbar({
            message: 'Failed to update basic student information', severity: 'error'
        })
    }[success]());

    const handleChangeValue = (value, field) => {
        validateValue(value, field);
        let changedProperty = {};
        changedProperty[field.key] = !value ? null : value;
        setProfileData({...profileData, ...changedProperty});
    };

    const validateValue = (value, field) => {
        if (field.type === 'textField') {
            const trimValue = value.trim();
            let newErrors = Object.assign(errors);

            if (field.isRequired && trimValue === '') {
                newErrors[field.key] = 'Field required';
            } else if (field.maxLength && trimValue.length > field.maxLength) {
                newErrors[field.key] = `The length should be less than ${field.maxLength}`;
            } else if (field.regex && !field.regex.test(trimValue) && !!trimValue) {
                newErrors[field.key] = 'Incorrect format';
            } else if ((field.required || value) && field.validate && !field.validate(value)) {
                newErrors[field.key] = field.validationErrorMessage;
            } else {
                delete newErrors[field.key];
            }
            setErrors(newErrors);
        }
    };

    const handleSave = () => {
        confirm({
            title: 'Confirmation',
            description: `I confirm that the information in the basic profile section of ${selectedProfile.name} ${selectedProfile.surname} student profile is correct and understand it will be relied upon by school staff and any other person or organisation who may have ${selectedProfile.name} ${selectedProfile.surname} in their care on behalf of ${currentSchool.name}`,
            confirmationText: 'Confirm'
        })
            .then(() => {
                if (user.role === ROLES.PARENT) {
                    dispatch(updateStudentByParent({
                        studentId: selectedProfile.id,
                        id: profileData.id,
                        studentDetailsDto: profileData
                    }));
                } else {
                    dispatch(updateBasicInfo({
                        studentId: selectedProfile.id,
                        id: profileData.id,
                        studentDetailsDto: profileData
                    }));
                }
            })
            .catch(() => {
            });
    };

    const handleConfirm = () => {
        confirm({
            title: 'Confirmation',
            description: `I confirm that the information in the basic profile section of ${selectedProfile.name} ${selectedProfile.surname} student profile is correct and understand it will be relied upon by school staff and any other person or organisation who may have ${selectedProfile.name} ${selectedProfile.surname} in their care on behalf of ${currentSchool.name}`,
            confirmationText: 'Confirm'
        })
            .then(() => {
                dispatch(confirmProfile({
                    studentId: selectedProfile.id,
                    type: STUDENT_PROFILE_CONFIRMATION_TYPES.Basic
                }));
            })
            .catch(() => {
            });
    };

    const handleCancel = () => {
        setProfileData(selectedProfile.basicInfo);
        setIsEditMode(false);
    };

    return (<>
        {Object.keys(profileData).length > 0 && <>
            <StudentMainInfo>
                <Avatar variant="square" sx={{width: 90, height: 120}}
                        alt={`${selectedProfile.name} ${selectedProfile.surname}`}
                        src={selectedProfile.photo && `data:image/jpeg;base64,${selectedProfile.photo}`}/>
                <Box display={'flex'} flexDirection={'column'} gap={2}>
                    <Box display={'flex'} justifyContent={'end'}>
                        {selectedProfile.isApplicant
                            ? <ProfileStatus status={selectedProfile.status}
                                             statusCategory={selectedProfile.statusCategory}
                                             isStudentProfile={true}/>
                            : <div></div>
                        }
                    </Box>
                    <StudentMainFields sx={{
                        flexDirection: {xs: 'column', sm: 'column', md: 'row', lg: 'row', xl: 'row'},
                        justifyContent: isSmallScreen ? 'start' : 'space-around',
                        gap: '10px'
                    }}>
                        <BasicInfoFields isEditMode={isEditMode} handleChangeValue={handleChangeValue}
                                         fields={basicInfoFields.studentInfo} profileData={profileData}
                                         errors={errors}/>

                    </StudentMainFields>
                </Box>
            </StudentMainInfo>
            <StudentDataBox>
                <EducationInfoSection>
                    <Typography variant="h5" fontWeight="bold" px={2}>Education information</Typography>
                    <Divider/>
                    <EducationInfoFields px={2} py={2} gap={2}>
                        <BasicInfoFields isEditMode={isEditMode} handleChangeValue={handleChangeValue}
                                         fields={buildFields()} profileData={profileData} errors={errors}
                                         selectOptions={buildSelectOptions()}/>
                    </EducationInfoFields>
                </EducationInfoSection>
                <HomeAddressContainer>
                    <Typography variant="h5" fontWeight="bold" px={2}>Home Address</Typography>
                    <Divider/>
                    <Grid container py={2} sx={{height: '100%'}}>
                        <AddressSectionFields item xs={12} sm={6} px={3}>
                            <BasicInfoFields isEditMode={isEditMode} handleChangeValue={handleChangeValue}
                                             fields={basicInfoFields.addressFirstSection} profileData={profileData}
                                             errors={errors}/>

                        </AddressSectionFields>
                        <AddressSectionFields item xs={12} sm={6} px={3}>
                            <BasicInfoFields isEditMode={isEditMode} handleChangeValue={handleChangeValue}
                                             fields={basicInfoFields.addressSecondSection} profileData={profileData}
                                             errors={errors}
                                             selectOptions={{
                                                 countryCode: countries.map(element => {
                                                     return {value: element.code, title: element.name};
                                                 }),
                                             }}/>
                        </AddressSectionFields>
                    </Grid>
                </HomeAddressContainer>
            </StudentDataBox>
            {
                readonly
                    ? ''
                    : <ActionButtonsBox>
                        {
                            profileData.editedAt && profileData.editedBy && <Typography mt={1}>Confirmed
                                at {formatDate(profileData.editedAt)} by {profileData.editedBy}</Typography>
                        }
                        {isEditMode
                            ? <>
                                <ActionButton variant="outlined" onClick={handleCancel}>Cancel</ActionButton>
                                <ActionButton disabled={!!Object.keys(errors).length} variant="contained"
                                              onClick={handleSave}>Save</ActionButton>
                            </>
                            : <>
                                {!profileData.editedAt &&
                                    <ActionButton variant="contained" onClick={handleConfirm}>Confirm</ActionButton>}
                                {user.role !== ROLES.PARENT && <ActionButton variant="contained" onClick={() => {
                                    setIsEditMode(true);
                                }}>Edit</ActionButton>}
                            </>}
                    </ActionButtonsBox>
            }
        </>}
    </>);
};

BasicInfoTab.propTypes = {
    readonly: PropTypes.bool
};

export default BasicInfoTab;
